// @ts-strict-ignore
import React, { useCallback, useMemo } from 'react'
import { useAtom, useAtomValue } from 'jotai'
import { isEmpty } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { Box, Button, Combobox, Box as Form, Input, Radio } from '@node-space/ui-components'
import { RequestErrorCallout } from 'components/Errors/RequestErrorCallout'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useSupportedCurrencies } from 'hooks/useSupportedCurrencies'
import { useWalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { ConvertWallet, WalletType } from 'types/types'
import { convertFormValuesAtom, convertFromWalletAtom, convertToWalletAtom } from './atoms/atoms'
import { CustomersDropdown } from './components/CustomersDropdown/CustomersDropdown'
import { CustomersWalletsDropdown } from './components/CustomersWalletsDropdown/CustomersWalletsDropdown'
import { useConvertDetails } from './hooks/useConvertDetails'
import { ConvertWalletTarget } from './schemas/convertDetailsSchema'

interface ConvertDetailsProps {
  convertFromCustomerWallet?: ConvertWallet
}

export const ConvertDetails = ({ convertFromCustomerWallet }: ConvertDetailsProps) => {
  const { t } = useTranslation()
  const { enableEpcWalletConversion } = useFeatureFlags()
  const { getWalletOptions } = useSupportedCurrencies()
  const [convertToWallet, setConvertToWallet] = useAtom(convertToWalletAtom)
  const convertFromWallet = useAtomValue(convertFromWalletAtom)
  const [convertFormValues, setConvertFormValues] = useAtom(convertFormValuesAtom)
  const [walletAction] = useWalletActionContext()

  const { requestError, formData, formStatus, formhandlers } =
    useConvertDetails(convertFromCustomerWallet)

  const isCustomerSource = !!convertFromCustomerWallet
  const selectedCustomerOption = useMemo(
    () =>
      convertFromCustomerWallet ? getWalletOptions([convertFromCustomerWallet as WalletType]) : [],
    [convertFromCustomerWallet]
  )

  const sourceWalletOptions = useMemo(
    () => getWalletOptions(formData?.sourceWallets),
    [formData?.sourceWallets]
  )

  const sourceToWalletOptions = useMemo(
    () => getWalletOptions(formData?.sourceToWallets),
    [formData?.sourceToWallets]
  )

  const selectedBothWallets = !isEmpty(convertFromWallet) && !isEmpty(convertToWallet)

  const handleToAmountOnChange = (value: string) => {
    formhandlers?.setValue('toAmount', value)
    setConvertFormValues({ ...convertFormValues, changedField: 'toAmount' })
  }

  const handleFromAmountOnChange = (value: string) => {
    formhandlers?.setValue('fromAmount', value)
    setConvertFormValues({ ...convertFormValues, changedField: 'fromAmount' })
  }

  const handleReferenceOnChange = (value: string) => {
    formhandlers?.setValue('reference', value)
  }

  const handleMaxAmount = () => {
    if (convertFromWallet?.balance) {
      const maxValue = convertFromWallet?.balance?.toString()
      formhandlers?.setValue('fromAmount', maxValue)
      setConvertFormValues({ ...convertFormValues, changedField: 'fromAmount' })
    }
  }

  const handleWalletTypeChange = useCallback(
    (walletType: string) => {
      formhandlers?.setValue('walletType', walletType as ConvertWalletTarget)
      setConvertToWallet(undefined)
      formhandlers?.setValue('fromAmount', '')
      formhandlers?.setValue('toAmount', '')
      setConvertFormValues({ ...convertFormValues, changedField: 'toAmount' })
    },
    [formhandlers, convertFormValues]
  )

  return (
    <Form
      tag="form"
      name="convert-details-form"
      flexCol
      gapY={16}
      paddingT={24}
      data-testid="convert-details-form"
      onSubmit={formhandlers?.handleSubmit(formhandlers?.onSubmit)}
    >
      {requestError?.show && <RequestErrorCallout requestError={requestError} />}

      {isCustomerSource ? (
        <>
          <Box width="full">
            <Combobox
              testId="from-wallet"
              key={`from-wallet-customer-source-${convertFromCustomerWallet?.lsid}`}
              {...formData?.formFields.fromWallet}
              items={selectedCustomerOption}
              defaultValue={convertFromWallet?.lsid}
              disabled
            />
          </Box>
          <Box width="full">
            <Combobox
              testId="to-wallet"
              key={`to-wallet-customer-source-${convertFromCustomerWallet?.lsid}`}
              {...formData?.formFields.toWallet}
              items={sourceToWalletOptions}
              loading={formStatus?.isFetchingWallets}
              loadingInline={formStatus?.isFetchingWallets}
              onChange={formhandlers?.handleToWalletOnChange}
              onInputChange={formhandlers?.setSearchWalletQuery}
              defaultValue={convertToWallet?.lsid}
              onOpen={formhandlers?.handleOpen}
              modal
            />
          </Box>
        </>
      ) : (
        <>
          <Box width="full">
            <Combobox
              testId="from-wallet"
              key={`from-wallet-${convertFromWallet?.lsid}`}
              {...formData?.formFields.fromWallet}
              items={sourceWalletOptions}
              loading={formStatus?.isFetchingWallets}
              loadingInline={formStatus?.isFetchingWallets}
              onChange={formhandlers?.handleFromWalletOnChange}
              onInputChange={formhandlers?.setSearchWalletQuery}
              defaultValue={convertFromWallet?.lsid ?? walletAction?.meta?.sourceWallet?.lsid}
              onOpen={formhandlers?.handleOpen}
              modal
            />
          </Box>
          {enableEpcWalletConversion && (
            <Box>
              <Radio
                testId="wallet-type"
                {...formData?.formFields.walletType}
                value={formData.formValues.walletType}
                onChange={handleWalletTypeChange}
                horizontal
              />
            </Box>
          )}
          {formData?.formValues.walletType === ConvertWalletTarget.Self && (
            <Box width="full">
              <Combobox
                testId="to-wallet"
                key={`to-wallet-${convertFromWallet?.lsid}`}
                {...formData?.formFields.toWallet}
                items={sourceToWalletOptions}
                loading={formStatus?.isFetchingWallets}
                loadingInline={formStatus?.isFetchingWallets}
                onChange={formhandlers?.handleToWalletOnChange}
                onInputChange={formhandlers?.setSearchWalletQuery}
                defaultValue={convertToWallet?.lsid}
                onOpen={formhandlers?.handleOpen}
                modal
              />
            </Box>
          )}
          {formData?.formValues.walletType === ConvertWalletTarget.Customer && (
            <>
              <Box width="full">
                <CustomersDropdown {...formData.formFields.customerId} />
              </Box>
              {!!formData.formValues.customerId && (
                <Box width="full">
                  <CustomersWalletsDropdown
                    {...formData.formFields.toWallet}
                    customerId={formData.formValues.customerId}
                  />
                </Box>
              )}
            </>
          )}
        </>
      )}
      {selectedBothWallets && (
        <Box flex gapX={12} width="full" justify="between">
          <Box width="full">
            <Input
              testId="from-amount"
              disabled={formStatus?.isFetchingLimits || formStatus?.isPostingQuoteEstimate}
              {...formData?.formFields.fromAmount}
              {...(!formStatus?.isFetchingLimits &&
                !formStatus?.isPostingQuoteEstimate && { helperText: formData?.minimumAmount })}
              onChange={event => handleFromAmountOnChange(event?.target?.value)}
              rightSuffixText={convertFromWallet?.currency?.code}
              LabelButton={
                <Button
                  testId="max-amount-button"
                  variant="ghost"
                  noPadding
                  disabled={
                    formStatus?.isFetchingLimits ||
                    formStatus?.isPostingQuoteEstimate ||
                    !convertFromWallet?.balance
                  }
                  onClick={handleMaxAmount}
                >
                  {t('max')}
                </Button>
              }
            />
          </Box>
          {!formData?.isSameCurrency && (
            <Box width="full">
              <Input
                testId="to-amount"
                disabled={formStatus?.isFetchingLimits || formStatus?.isPostingQuoteEstimate}
                {...formData?.formFields.toAmount}
                onChange={event => handleToAmountOnChange(event?.target?.value)}
                rightSuffixText={convertToWallet?.currency?.code}
              />
            </Box>
          )}
        </Box>
      )}

      <Box width="full">
        <Input
          testId="reference"
          name="reference"
          {...formData?.formFields.reference}
          label={`${t('paymentReference')} (${t('optional')})`}
          disabled={formStatus?.isValidatingReference}
          onBlur={formhandlers?.handleReferenceValidate}
          onChange={event => handleReferenceOnChange(event?.target?.value)}
        />
      </Box>

      <Box flex justify="between" paddingT={8}>
        <Button testId="back" variant="secondary" onClick={walletAction?.onClose}>
          {t('cancel')}
        </Button>

        <Button
          testId="request-quote"
          type="submit"
          variant="primary"
          loading={formStatus?.isLoadingRequestQuote}
          disabled={formStatus?.isDisabledRequestQuote}
        >
          {t('wallets.convert.requestQuote')}
        </Button>
      </Box>
    </Form>
  )
}
